/**
 * Pazarlama çalışma merkezi metinleri (24 Ekim 2026). Ürün dili: kaynak, kampanya, kesinlik ve atıf ayrı kavramlardır; kazanılan
 * ciro tahsilat değildir; manuel potansiyel ölçülmüş kazanma olasılığı değildir. Asistanın teknik altyapı adı arayüzde geçmez
 * (kuruluşun verdiği ad, yoksa "Asistan").
 */
import type { AttributionModel, MarketingProcessStage } from '@castinflow/shared';
import type { MarketingMetricKey, MarketingRateKey } from '@castinflow/shared';

export const marketingText = {
  nav: 'Çalışma merkezi',
  title: 'Pazarlama çalışma merkezi',
  description:
    'Kaynaktan sonraki işe: kim yanıt bekliyor, kime atanmalı, hangi kaynak ve kampanya görüşmeye ve satışa dönüşüyor.',
  // Telefonda açıklama gizli; ekran okuyucu ve masaüstü için aynı metin
  crumb: 'Pazarlama',
  forbidden: 'Bu ekranı görmek için aday okuma yetkiniz olmalı.',
  loadFailed: 'Çalışma merkezi yüklenemedi.',
  retry: 'Yeniden dene',
  demo: 'Demo çalışma alanı: sayılar örnek veridir; gerçek reklam, ileti ya da arama yoktur.',

  // --- Süzgeçler ---
  filters: {
    title: 'Süzgeçler',
    open: 'Süzgeçler',
    apply: 'Uygula',
    reset: 'Varsayılana dön',
    period: 'Dönem',
    from: 'Başlangıç',
    to: 'Bitiş',
    source: 'Kaynak',
    /** Kaynak süzgeci seçilen atıf modeline göredir (2. tur § 6) */
    sourceFor: (m: AttributionModel) =>
      m === 'first_touch' ? 'Kaynak (ilk temas)' : 'Kaynak (son temas)',
    modelShort: (m: AttributionModel) => (m === 'first_touch' ? 'İlk temas' : 'Son temas'),
    /** Adayın kayıt kaynağı süzgeci (`recordSource`) — temastan bağımsız başlangıç kaynağı */
    recordSource: 'Kayıt kaynağı',
    recordSourceSummary: (label: string) => `Kayıt kaynağı: ${label}`,
    allSources: 'Tüm kaynaklar',
    campaign: 'Kampanya',
    allCampaigns: 'Tüm kampanyalar',
    noCampaign: 'Kampanya bilinmiyor',
    campaignNameMissing: 'Kampanya adı alınamadı',
    model: 'Atıf',
    presets: { '7': 'Son 7 gün', '30': 'Son 30 gün', '90': 'Son 90 gün', custom: 'Özel aralık' },
    rangeInvalid: 'Başlangıç bitişten sonra olamaz ve aralık en çok 400 gün olabilir.',
    periodText: (from: string, to: string, tz: string) =>
      `${from} – ${to} (${tz}; adayın oluşturulma günü)`,
    passive: (label: string) => `${label} (pasif)`,
    deletedSource: 'Silinmiş kaynak',
    notFoundSource: 'Kaynak bilgisi bulunamadı',
    unknownSource: 'Kaynak bilinmiyor',
    sourceNamesFailed: 'Kaynak adları alınamadı',
    diagnosticKey: (key: string) => `Teşhis: kaynak anahtarı ${key}`,
    summaryLabel: 'Uygulanan süzgeçler',
  },

  // --- Ölçü kartları ---
  metrics: {
    title: 'Dönemde gelen adaylar ve bugünkü sonuçları',
    label: {
      incoming: 'Gelen adaylar',
      new: 'Yeni',
      unassigned: 'Atama bekleyen',
      awaitingReply: 'Yanıt bekleyen',
      priority: 'Öncelikli',
      qualified: 'Nitelikli',
      converted: 'Fırsata dönüşen',
      won: 'Satışa dönüşen',
    } satisfies Record<MarketingMetricKey, string>,
    hint: {
      incoming: 'Dönemde oluşan; durumdan bağımsız',
      new: 'Yeni durumda, dönüşmemiş',
      unassigned: 'Sorumlusu yok, açık',
      awaitingReply: 'Müşteri yanıt bekliyor',
      priority: (n: number) => `Potansiyel %${String(n)} ve üzeri`,
      qualified: 'Nitelikli durumda',
      converted: 'Fırsat ilişkisi olan',
      won: 'Fırsatı kazanılan',
    },
    notCollected: 'tahsilat değil',
  },

  // --- Bugün odağı (yalnız gerçek sayıdan; yoksa bant çizilmez) ---
  focus: {
    label: 'Bugünün odağı',
    awaitingReply: (n: string) => `${n} aday yanıt bekliyor — önce onlarla başlayın.`,
    unassigned: (n: string) => `${n} aday sorumlu bekliyor — atamayı tamamlayın.`,
    priority: (n: string, t: number) =>
      `${n} açık aday öncelik eşiğinde (potansiyel %${String(t)} ve üzeri).`,
    new: (n: string) => `${n} aday hâlâ yeni durumda — ilk teması planlayın.`,
    priorityNote: 'Öncelik temsilcinin değerlendirmesinden gelir; satış garantisi değildir.',
    /** Kullanıcı başka sekme seçtiyse öneri ikincil bağlantı olarak kalır (seçimle çelişen cümle yazılmaz) */
    suggestion: (tab: string, n: string) => `Önerilen: ${tab} (${n})`,
    open: {
      awaitingReply: 'Yanıt bekleyenleri göster',
      unassigned: 'Atama bekleyenleri göster',
      priority: 'Öncelikli adayları göster',
      new: 'Yeni adayları göster',
    },
  },

  // --- Çalışma kuyruğu ---
  queue: {
    title: 'Çalışma kuyruğu',
    description: 'Bugün kiminle ilgilenmeli? Satırdan çıkmadan inceleyin, kaydedin, takip açın.',
    tabsLabel: 'Kuyruk',
    tabs: {
      awaitingReply: 'Yanıt bekleyen',
      unassigned: 'Atama bekleyen',
      priority: 'Öncelikli',
      new: 'Yeni',
    },
    seeAll: (n: string) => `Tümünü listede gör (${n})`,
    empty: {
      awaitingReply: 'Yanıt bekleyen aday yok.',
      unassigned: 'Atama bekleyen aday yok.',
      priority: 'Öncelik eşiğini geçen açık aday yok.',
      new: 'Bu dönemde yeni durumda aday yok.',
    },
    loadFailed: 'Kuyruk yüklenemedi.',
    conversationsFailed: 'Görüşme bilgisi alınamadı; görüşme bağlantıları gösterilmiyor.',
    owner: 'Sorumlu',
    noOwner: 'Atanmadı',
    assistantRuns: (name: string) => `${name} yürütüyor`,
    assistantFallback: 'Asistan',
    source: 'Kaynak',
    campaign: 'Kampanya',
    lastContact: 'Son temas',
    noContact: 'Temas yok',
    nextFollowUp: 'Sonraki takip',
    noFollowUp: 'Takip yok',
    overdue: 'Gecikti',
    potential: 'Potansiyel',
    converted: 'Fırsata dönüştü',
    preview: 'Önizle',
    moreDetails: 'Ayrıntılar',
    lessDetails: 'Daha az',
    log: 'Aktivite kaydet',
    followUp: 'Takip',
    openConversation: 'Görüşmeyi aç',
    actions: (name: string) => `${name} için işlemler`,
    logged: 'Aktivite kaydedildi.',
    loggedWithFollowUp: 'Aktivite ve sonraki takip kaydedildi.',
    loggedReplayed: 'Bu aktivite zaten kaydedilmişti.',
    taskCreated: 'Takip görevi oluşturuldu.',
  },

  // --- Grafikler ---
  charts: {
    funnelTitle: 'Süreç görünümü',
    funnelNote:
      'Her satır, dönemde gelen adaylardan bu koşulu bugün sağlayanların sayısıdır; ortak ölçekle çizilir. Satırlar birbirini kapsamaz: ileri adımdaki kayıt önceki adımlardan geçmiş sayılmaz. Her satır kendi listesine açılır.',
    ratesTitle: 'Oranlar',
    rateName: {
      leadToOpportunity: 'Adaydan fırsata',
      opportunityToWon: 'Fırsattan satışa',
      leadToWon: 'Adaydan satışa',
    } satisfies Record<MarketingRateKey, string>,
    rateText: {
      leadToOpportunity: (a: string, b: string) => `${a} / ${b} aday fırsata dönüştü`,
      opportunityToWon: (a: string, b: string) => `${a} / ${b} fırsat kazanıldı`,
      leadToWon: (a: string, b: string) => `${a} / ${b} aday satışa dönüştü`,
    } satisfies Record<MarketingRateKey, (a: string, b: string) => string>,
    noDenominator: 'hesaplanacak kayıt yok',
    ratesNote: 'Pay ve payda aynı dönem kümesindendir; para birimleri oranlara girmez.',
    stage: {
      incoming: 'Gelen',
      contacted: 'Temas kurulan',
      qualified: 'Nitelikli',
      converted: 'Fırsata dönüşen',
      won: 'Kazanılan',
    } satisfies Record<MarketingProcessStage, string>,
    sourcesTitle: (m: AttributionModel) =>
      m === 'first_touch' ? 'Kaynak (ilk temas)' : 'Kaynak (son temas)',
    sourcesNote:
      'Seçilen atıf modelinin temas kaynağı; temas kaydı olmayan adayda kayıt kaynağı kullanılır (kampanya raporuyla aynı kural). Kaynak payı kalite değildir.',
    recordSourcesTitle: 'Kayıt kaynağı',
    recordSourcesNote: 'Aday kaydında yazan başlangıç kaynağı; temastan bağımsızdır.',
    sourcesEmpty: 'Bu dönemde kaynak bilgisi olan aday yok.',
    trendTitle: 'Dönemsel gelişim',
    trendNote: (bucket: 'day' | 'week') =>
      `${bucket === 'day' ? 'Günlük' : 'Haftalık'} gelen aday. Önceki dönemle karşılaştırma yapılmaz; son kova sürüyorsa "devam ediyor" yazar.`,
    trendSeries: 'Gelen aday',
    partial: 'devam ediyor',
    tableToggle: 'Tablo olarak göster',
    colStage: 'Aşama',
    colSource: 'Kaynak',
    colPeriod: 'Dönem',
    colCount: 'Aday',
    open: (label: string) => `${label} listesini aç`,
    leads: (n: string) => `${n} aday`,
  },

  // --- Kampanya kalitesi ---
  campaigns: {
    title: 'Kampanya kalitesi',
    description:
      'Kampanyadan yolculuğa: aday → nitelikli → fırsat → kazanılan. Sayıya tıklayınca süzülmüş aday listesi açılır; adaydan önizlemeyle kişi, görüşme ve satışa geçilir.',
    model: (m: string) => `Atıf: ${m}`,
    leads: 'Aday',
    qualified: 'Nitelikli',
    opportunities: 'Fırsat',
    won: 'Kazanılan',
    revenue: 'Kazanılan ciro',
    spend: 'Harcama',
    roas: 'Reklam getirisi',
    noCampaign: 'Kampanya bilinmiyor',
    empty: 'Bu dönemde kampanya satırı yok.',
    loadFailed: 'Kampanya karşılaştırması yüklenemedi.',
    spendMissing: 'Harcama bağlantısı yok: maliyet ve reklam getirisi hesaplanmaz.',
    spendNoMatch:
      'Reklam hesabı bağlı, ancak bu dönemin kampanyalarıyla eşleşen harcama verisi yok: maliyet ve reklam getirisi hesaplanmaz.',
    spendCoverage: (pct: string) =>
      `Harcaması bilinen kampanyalar adayların %${pct} kadarını kapsıyor; getiri yalnız kapsam tamsa yazılır.`,
    revenueNote:
      'Ciro kazanılan fırsatların tutarıdır, tahsilat değildir; para birimleri toplanmaz.',
    openReport: 'Kampanya raporunu aç',
    opportunitiesHint: 'Fırsat sayıları fırsat kaydının temasına göredir; aday listesine açılmaz.',
    sourceScopedNoLink:
      'Kaynak süzgeci seçiliyken kampanya sayıları listeye açılmaz: rapor temasın kaynağıyla, liste adayın kaynağıyla süzer.',
    openLeads: (campaign: string, n: string) => `${campaign}: ${n} aday — listeyi aç`,
  },

  // --- Önizleme: yolculuk ---
  journey: {
    title: 'Müşteri yolculuğu',
    source: 'Kaynak ve kampanya',
    sourceFailed: 'Kaynak bilgisi alınamadı.',
    noTouch: 'Bu adayın kayıtlı teması yok.',
    conversation: 'Görüşme',
    noConversation: 'Bu adaya bağlı görüşme yok.',
    human: 'Temsilci yürütüyor',
    awaiting: 'Yanıt bekliyor',
    conversion: 'Kişi, şirket ve fırsat',
    notConverted: 'Henüz kişiye ya da fırsata dönüştürülmedi.',
    contact: 'Kişi',
    company: 'Şirket',
    opportunity: 'Fırsat',
    salesNote: 'Teklif, sipariş ve satış sonucu fırsat kaydında izlenir.',
    openInbox: 'Gelen kutusunda aç',
  },

  // --- Boş durum ---
  empty: {
    title: 'Bu dönemde aday yok',
    body: 'Süzgeçleri genişletin ya da aday kaynaklarını bağlayın.',
    addLead: 'Aday ekle',
    endpoints: 'Aday uç noktası kur',
  },
} as const;
